/**
 * Per-object motion blur from the G-buffer velocity channel, with tile-max /
 * neighbour-max dilation so fast objects blur past their own silhouette.
 *
 * Samples are rejected across the viewmodel flag boundary, so the world's
 * camera blur never smears the weapon and vice versa.
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const TILE_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tSrc;
uniform vec2 uStep;
uniform float uUseData;   // 1 = read velocity from gData.rg, 0 = from rg of a tile buffer
varying vec2 vUv;
void main() {
  vec2 best = vec2(0.0);
  float bestLen = -1.0;
  for (int i = 0; i < PF_TILE; i++) {
    vec2 uv = vUv + uStep * (float(i) - float(PF_TILE) * 0.5 + 0.5);
    vec2 v = texture2D(tSrc, uv).rg;
    float l = dot(v, v);
    if (l > bestLen) { bestLen = l; best = v; }
  }
  gl_FragColor = vec4(best, 0.0, 1.0);
}
`;

const NEIGHBOR_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tSrc;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec2 best = vec2(0.0);
  float bestLen = -1.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 v = texture2D(tSrc, vUv + vec2(float(x), float(y)) * uTexel).rg;
      float l = dot(v, v);
      if (l > bestLen) { bestLen = l; best = v; }
    }
  }
  gl_FragColor = vec4(best, 0.0, 1.0);
}
`;

const BLUR_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform sampler2D tData;
uniform sampler2D tNeighbor;
uniform sampler2D tNoise;
uniform vec2 uTexel;
uniform vec2 uNoiseScale;
uniform float uShutter;
uniform float uMaxBlurPx;
uniform float uFrame;
varying vec2 vUv;

void main() {
  vec3 color = max(texture2D(tColor, vUv).rgb, vec3(0.0));
  vec2 nv = texture2D(tNeighbor, vUv).rg * uShutter;
  vec2 nvPx = nv / uTexel;
  float nvLen = length(nvPx);
  if (nvLen < 1.0) { gl_FragColor = vec4(color, 1.0); return; }
  if (nvLen > uMaxBlurPx) { nv *= uMaxBlurPx / nvLen; nvLen = uMaxBlurPx; }

  vec4 cd = texture2D(tData, vUv);
  float cz = cd.b > 0.0 ? cd.b : 1e5;
  float cflag = cd.a;
  vec2 cv = cd.rg * uShutter;
  float cvLen = length(cv / uTexel);

  float jitter = fract(texture2D(tNoise, vUv * uNoiseScale).b + uFrame * 0.6180339887498949);

  float wsum = 1.0 / max(cvLen, 1.0);
  vec3 sum = color * wsum;
  const float softZ = 1.2;

  for (int i = 0; i < PF_MB_TAPS; i++) {
    float t = ((float(i) + jitter) / float(PF_MB_TAPS)) * 2.0 - 1.0;
    vec2 uv = vUv + nv * t;
    vec4 sd = texture2D(tData, uv);
    if (abs(sd.a - cflag) > 0.5) continue;
    float sz = sd.b > 0.0 ? sd.b : 1e5;
    float svLen = length(sd.rg * uShutter / uTexel);
    float dpx = abs(t) * nvLen;

    float front = saturate(1.0 + (cz - sz) / softZ);
    float back = saturate(1.0 + (sz - cz) / softZ);
    float w = front * saturate(svLen - dpx + 1.0) + back * saturate(cvLen - dpx + 1.0);
    if (w <= 0.0) continue;
    sum += max(texture2D(tColor, uv).rgb, vec3(0.0)) * w;
    wsum += w;
  }

  gl_FragColor = vec4(sum / max(wsum, 1e-4), 1.0);
}
`;

const K = 20;

export class MotionBlur {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.tileH = null; this.tileRT = null; this.neighborRT = null; this.outRT = null;
    this.w = 0; this.h = 0;

    this.matTile = makeMaterial({
      name: 'postfx.mb.tile',
      fragmentShader: TILE_FRAG,
      defines: { PF_TILE: K },
      uniforms: { tSrc: { value: null }, uStep: { value: new THREE.Vector2() }, uUseData: { value: 1 } },
    });
    this.matNeighbor = makeMaterial({
      name: 'postfx.mb.neighbor',
      fragmentShader: NEIGHBOR_FRAG,
      uniforms: { tSrc: { value: null }, uTexel: { value: new THREE.Vector2() } },
    });
    this.matBlur = makeMaterial({
      name: 'postfx.mb.blur',
      fragmentShader: BLUR_FRAG,
      defines: { PF_MB_TAPS: 12 },
      uniforms: {
        tColor: { value: null }, tData: { value: null }, tNeighbor: { value: null }, tNoise: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uNoiseScale: { value: new THREE.Vector2() },
        uShutter: { value: 0.55 },
        uMaxBlurPx: { value: 48.0 },
        uFrame: { value: 0 },
      },
    });
  }

  setQuality(taps) {
    if (this.matBlur.defines.PF_MB_TAPS === taps) return;
    this.matBlur.defines.PF_MB_TAPS = taps;
    this.matBlur.needsUpdate = true;
  }

  setSize(w, h) {
    if (this.w === w && this.h === h) return;
    this.w = w; this.h = h;
    const tw = Math.max(1, Math.ceil(w / K)), th = Math.max(1, Math.ceil(h / K));
    for (const rt of [this.tileH, this.tileRT, this.neighborRT, this.outRT]) if (rt) rt.dispose();
    this.tileH = makeRT(tw, h, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, name: 'postfx.mb.tileH' });
    this.tileRT = makeRT(tw, th, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, name: 'postfx.mb.tile' });
    this.neighborRT = makeRT(tw, th, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, name: 'postfx.mb.neighbor' });
    this.outRT = makeRT(w, h, { name: 'postfx.mb.out' });
  }

  render(renderer, p) {
    const t = this.matTile.uniforms;
    t.tSrc.value = p.dataTexture;
    t.uStep.value.set(1 / this.w, 0);
    this.blit.draw(renderer, this.matTile, this.tileH);
    t.tSrc.value = this.tileH.texture;
    t.uStep.value.set(0, 1 / this.h);
    this.blit.draw(renderer, this.matTile, this.tileRT);

    const n = this.matNeighbor.uniforms;
    n.tSrc.value = this.tileRT.texture;
    n.uTexel.value.set(1 / this.tileRT.width, 1 / this.tileRT.height);
    this.blit.draw(renderer, this.matNeighbor, this.neighborRT);

    const b = this.matBlur.uniforms;
    b.tColor.value = p.colorTexture;
    b.tData.value = p.dataTexture;
    b.tNeighbor.value = this.neighborRT.texture;
    b.tNoise.value = p.noiseTexture;
    b.uTexel.value.set(1 / this.w, 1 / this.h);
    b.uNoiseScale.value.set(this.w / (p.noiseSize || 64), this.h / (p.noiseSize || 64));
    b.uShutter.value = p.shutter;
    b.uFrame.value = p.frame % 64;
    this.blit.draw(renderer, this.matBlur, this.outRT);
    return this.outRT.texture;
  }

  dispose() {
    for (const rt of [this.tileH, this.tileRT, this.neighborRT, this.outRT]) if (rt) rt.dispose();
    this.matTile.dispose(); this.matNeighbor.dispose(); this.matBlur.dispose();
  }
}
